import { LockIcon, PlusIcon, XIcon } from 'lucide-react';

import { AtlasIcon, AtlasIcons } from '@crossnokaye/ux-animatics';
import { TableCell } from '@crossnokaye/ux-common';

import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Stack } from '@/ux-common/components/stack/stack';

import { IconWithTooltip } from './icon-wrapper';

interface EquipmentCellProps {
  icon: keyof typeof AtlasIcons;
  isLockedByOther?: boolean;
  isRowDisabled: boolean;
  leadingAdornment?: React.ReactNode;
  lockedByLabel?: string;
  onAddRow: () => void;
  onRemoveRow: () => void;
  rowTitle: string;
}

export const EquipmentCell = ({
  icon,
  isLockedByOther,
  isRowDisabled,
  leadingAdornment,
  lockedByLabel,
  onAddRow,
  onRemoveRow,
  rowTitle,
}: EquipmentCellProps) => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  return (
    <TableCell className="gap-6 px-2 flex items-center justify-between h-[inherit] border-l-0 border-b-1">
      <div className={'flex items-center justify-left gap-2 text-secondary-foreground'}>
        <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
          <AtlasIcon symbol={icon} size="1rem" className="fill-ckds-text-fg-tier3" />
          {leadingAdornment}
        </span>
        {rowTitle}
      </div>
      <IconWithTooltip
        icon={isLockedByOther && isRowDisabled ? LockIcon : isRowDisabled ? PlusIcon : XIcon}
        isDisabled={disabledByPermissions || (!!isLockedByOther && isRowDisabled)}
        tooltipArrow
        onClick={() => {
          if (disabledByPermissions) return;
          if (isLockedByOther && isRowDisabled) return;
          if (isRowDisabled) {
            onAddRow();
          } else {
            onRemoveRow();
          }
        }}
        tooltipContent={
          <Stack className="stack-thin">
            <span className="font-bold">
              {isLockedByOther && isRowDisabled
                ? `Assigned to ${lockedByLabel}`
                : isLockedByOther && !isRowDisabled
                  ? `Assigned to ${lockedByLabel} — remove to resolve`
                  : isRowDisabled
                    ? 'Enable Equipment'
                    : 'Disable Equipment'}
            </span>
            <span>{!isLockedByOther && disabledByPermissions ? tooltipText : undefined}</span>
          </Stack>
        }
      />
    </TableCell>
  );
};
